import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useI18n } from '../i18n';
import { inlineError } from '../hooks/useApiCall';
import { useBusy } from '../hooks/useBusy';
import { usePageTitle } from '../hooks/usePageTitle';
import { CheckIcon, XMarkIcon } from '../components/shared/Icons';
import AuthCard from '../components/auth/AuthCard';
import AuthResult from '../components/auth/AuthResult';
import Notice from '../components/shared/Notice';
import Field from '../components/shared/Field';

export default function ResetPassword() {
  const { t } = useI18n();
  usePageTitle(t.home.resetPassword);
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  // #605, #664 — the lock of Reset: a second tap sends no second reset.
  const reset = useBusy();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError(t.home.passwordsMismatch); return; }
    await reset.run(async () => {
      setError('');
      try {
        await api.post('/api/auth/email/reset-password', { token, password });
        setDone(true);
      } catch (err) {
        setError(inlineError(err));
      }
    });
  }

  if (!token) {
    return (
      <AuthCard>
        <AuthResult icon={XMarkIcon} tone="error" text={t.home.invalidResetLink}
          action={{ to: '/forgot-password', label: t.home.requestNewLink }} />
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t.home.newPassword}>
      {done ? (
        <AuthResult icon={CheckIcon} tone="success" text={t.home.passwordResetSuccess}
          action={{ to: '/login', label: t.auth.backToLogin }} />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Field label={t.home.newPassword} help={t.home.passwordRequirements}>
            <input type="password" required minLength={8} className="input" value={password}
              onChange={e => setPassword(e.target.value)} autoFocus data-testid="reset-password" />
          </Field>
          <Field label={t.home.confirmPassword}>
            <input type="password" required minLength={8} className="input" value={confirm}
              onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" />
          </Field>
          {error && <Notice tone="error">{error}</Notice>}
          <button type="submit" disabled={reset.busy} className="btn-primary w-full py-2.5" data-testid="reset-submit">
            {reset.busy ? t.common.loading : t.home.resetPassword}
          </button>
        </form>
      )}
    </AuthCard>
  );
}
